/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

/** 
  All of the routes for the Material Dashboard 2 PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/


// CRUDS
import UserProfile from "cruds/user-profile";
import RoleManagement from "cruds/role-managament";
import UserManagement from "cruds/user-management";
import OrganizationManagement from "cruds/user-management/organization-management";
import Dashboard from "layouts/dashboard";
import ClientsDashboard from "layouts/clients-dashboard";

import NewPartCode from "cruds/part-code-management/part-categories/create-part-category";
import NewPart from "cruds/part-code-management/part-codes/create-part-code";
import PartCodeView from "cruds/part-code-management/part-categories";
import PartView from "cruds/part-code-management/part-codes";
import ApprovalPage from "cruds/part-code-management/approval-page";

import InventoryView from "cruds/Inventory-management";
import CreateInventoryItem from "cruds/Inventory-management/create-inventory-item";
import DownloadReport from "cruds/Inventory-management/download-report";
import ViewReservations from "cruds/Inventory-management/view-reservations";

import LocationsView from "cruds/location-management";
import AddLocation from "cruds/location-management/add-location";

import AddProduct from "cruds/fulfillment-center/add-product";
import ViewYourProducts from "cruds/fulfillment-center/your-products";
import ManageOrders from "cruds/fulfillment-center/manage-orders";
import ViewOrders from "cruds/fulfillment-center/view-orders";
import ManageDispatches from "cruds/fulfillment-center/manage-dispatches";
import ViewDispatches from "cruds/fulfillment-center/view-dispatches";
import DispatchedProducts from "cruds/fulfillment-center/dispatched-products";

import ArchivedPartCodes from "cruds/archive-management/archived-part-codes";
import ArchivedPartCategories from "cruds/archive-management/archived-part-categories";

import PartCodeChanges from "cruds/changelog-management/part-code-changes";
import ProductChanges from "cruds/changelog-management/product-changes";
import PartCategoryChanges from "cruds/changelog-management/part-category-changes";
import OrderChanges from "cruds/changelog-management/order-changes";
import ItemChanges from "cruds/changelog-management/item-changes";

// Material Dashboard React components
import MDAvatar from "components/MDAvatar";

// @mui icons
import Icon from "@mui/material/Icon";

// Images
import profilePicture from "assets/images/team-3.jpg";

const routes = [
  {
    type: "collapse",
    name: "Bruce Mars",
    key: "user-name",
    icon: <MDAvatar src={profilePicture} alt="Bruce Mars" size="sm" />,
    collapse: [
      {
        name: "User Profile",
        key: "user-profile",
        route: "/examples-api/user-profile",
        component: <UserProfile />,
      },
      {
        name: "Logout",
        key: "logout",
      },
    ],
  },
  { type: "divider", key: "divider-1" },
  {
    type: "collapse",
    name: "Dashboards",
    key: "dashboards",
    icon: <Icon fontSize="medium">dashboard</Icon>,
    collapse: [
      {
        name: "E4A-Dashboard",
        key: "dashboard",
        route: "/dashboard",
        component: <Dashboard />,
        type: "dashboard"
      },
      {
        name: "Dashboard",
        key: "clients-dashboard",
        route: "/clients-dashboard",
        component: <ClientsDashboard />,
        type: "clients-dashboard"
      }
    ],
  },
  {
    type: "collapse",
    name: "Manage Part Codes",
    key: "part-code-management",
    icon: <Icon fontSize="medium">badge</Icon>,
    collapse: [
      {
        name: "View Part Codes",
        key: "part-code-management",
        route: "/part-code-management",
        type: "part-code-management",
        component: <PartView />,
      },
      {
        name: "View Part Categories",
        key: "part-category-view",
        icon: <Icon fontSize="medium"> star </Icon>,
        route: "/part-code-management/part-category-view",
        type: "part-code-management",
        component: <PartCodeView />,
      },
      {
        name: "Create Part Code",
        key: "create-part-code",
        route: "/part-code-management/create-part-code",
        component: <NewPart />,
        type: "create-part-code"
      },
      {
        name: "Create Part Category",
        key: "create-part-category",
        route: "/part-code-management/create-part-category",
        component: <NewPartCode />,
        type: "create-part-category"
      },
      {
        name: "Approval Page",
        key: "approval-page",
        route: "/part-code-management/approval-page",
        component: <ApprovalPage />,
        type: "approval-page"
      }
    ],
  },
  {
    type: "collapse",
    name: "Manage Inventory",
    key: "inventory-management",
    icon: <Icon fontSize="medium">inventory_2</Icon>,
    collapse: [
      {
        name: "View Inventory",
        key: "inventory-management",
        route: "/inventory-management",
        component: <InventoryView />,
        type: "inventory-management"
      },
      {
        name: "View Reservations",
        key: "view-reservations",
        route: "/inventory-management/view-reservations",
        component: <ViewReservations />,
        type: "inventory-management"
      },
      {
        name: "Create Inventory Item",
        key: "create-inventory-item",
        route: "/inventory-management/create-inventory-item",
        component: <CreateInventoryItem />,
        type: "create-inventory-item"
      },
      {
        name: "Download Inventory",
        key: "download-inventory-report",
        route: "/inventory-management/download-inventory-report",
        component: <DownloadReport />,
        type: "inventory-management"
      }
    ],
  },
  {
    type: "collapse",
    name: "Archive Management",
    key: "archive-management",
    icon: <Icon fontSize="medium">archive</Icon>,
    collapse: [
      {
        name: "Archived Part Categories",
        key: "archived-part-categories",
        route: "/archive-management/archived-part-categories",
        component: <ArchivedPartCategories />,
        type: "archive-management"
      },
      {
        name: "Archived Part Codes",
        key: "archived-part-codes",
        route: "/archive-management/archived-part-codes",
        component: <ArchivedPartCodes />,
        type: "archive-management"
      },
    ]
  },
  {
    type: "collapse",
    name: "Manage Locations",
    key: "location-management",
    icon: <Icon fontSize="medium">map</Icon>,
    collapse: [
      {
        name: "View Locations",
        key: "location-management",
        route: "/location-management",
        component: <LocationsView />,
        type: "location-management"
      },
      {
        name: "Add Location",
        key: "add-location",
        component: <AddLocation />,
        route: "/location-management/add-location",
        type: "add-location"
      }
    ]
  }, {
    type: "collapse",
    name: "Fulfillment Center",
    key: "fulfillment-center",
    icon: <Icon fontSize="medium">store</Icon>,
    collapse: [
      {
        name: "Your Products",
        key: "fulfillment-center",
        route: "/fulfillment-center",
        component: <ViewYourProducts />,
        type: "fulfillment-center"
      },
      {
        name: "Dispatched Products",
        key: "dispatched-products",
        route: "/fulfillment-center/dispatched-products",
        component: <DispatchedProducts />,
        type: "fulfillment-center"
      },
      {
        name: "Add Product",
        key: "add-product",
        route: "/fulfillment-center/add-product",
        component: <AddProduct />,
        type: "add-product"
      },
      {
        name: "View Orders",
        key: "view-orders",
        route: "/fulfillment-center/view-orders",
        component: <ViewOrders />,
        type: "fulfillment-center",
      },
      {
        name: "Manage Orders",
        key: "manage-orders",
        route: "/fulfillment-center/manage-orders",
        component: <ManageOrders />,
        type: "manage-orders",
      },
      {
        name: "View Dispatches",
        key: "view-dispatches",
        route: "/fulfillment-center/view-dispatches",
        component: <ViewDispatches />,
        type: "fulfillment-center",
      },
      {
        name: "Manage Dispatches",
        key: "manage-dispatches",
        route: "/fulfillment-center/manage-dispatches",
        component: <ManageDispatches />,
        type: "manage-dispatches",
      },
    ]
  },
  { type: "divider", key: "divider-2" },
  { type: "title", title: "User Management", key: "user-pages", name: "react-nodejs" },
  {
    type: "collapse",
    name: "User Management",
    key: "user-pages",
    icon: <Icon fontSize="medium">manage_accounts</Icon>,
    collapse: [
      {
        name: "User Management",
        key: "user-management",
        route: "/examples-api/user-management",
        component: <UserManagement />,
        type: "user-pages",
      },
      {
        name: "Role Management",
        key: "role-management",
        route: "/examples-api/role-management",
        component: <RoleManagement />,
        type: "user-pages",
      },
      {
        name: "Organization Management",
        key: "organization-management",
        route: "/examples-api/organization-management",
        component: <OrganizationManagement />,
        type: "user-pages",
      }
    ],
  },
  {
    type: "collapse",
    name: "Change Logs",
    key: "change-logs",
    icon: <Icon fontSize="medium">history</Icon>,
    collapse: [
      {
        name: "Part Category Changes",
        key: "part-category-changes",
        route: "/change-logs/part-category-changes",
        component: <PartCategoryChanges />,
        type: "change-logs",
      },
      {
        name: "Part Code Changes",
        key: "part-code-changes",
        route: "/change-logs/part-code-changes",
        component: <PartCodeChanges />,
        type: "change-logs",
      },
      {
        name: "Inventory Changes",
        key: "item-changes",
        route: "/change-logs/item-changes",
        component: <ItemChanges />,
        type: "change-logs",
      },
      {
        name: "Product Changes",
        key: "product-changes",
        route: "/change-logs/product-changes",
        component: <ProductChanges />,
        type: "change-logs",
      },
      {
        name: "Order Changes",
        key: "order-changes",
        route: "/change-logs/order-changes",
        component: <OrderChanges />,
        type: "change-logs",
      }
    ]
  }

];

export default routes;
